/**
 * Result Card Generator Service
 * Creates visually appealing game result cards for sharing
 */

import { GameResult, Player } from './MessageTemplates';

export interface ResultCardConfig {
  width?: number;
  height?: number;
  theme?: 'light' | 'dark' | 'auto';
  includeAvatar?: boolean;
  includeBranding?: boolean;
  customColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
  };
}

export interface GeneratedResultCard {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
}

export class ResultCardGenerator {
  private static instance: ResultCardGenerator;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
  }

  public static getInstance(): ResultCardGenerator {
    if (!ResultCardGenerator.instance) {
      ResultCardGenerator.instance = new ResultCardGenerator();
    }
    return ResultCardGenerator.instance;
  }

  /**
   * Generate a result card for Tic-Tac-Toe game
   */
  public async generateTicTacToeCard(
    result: GameResult,
    config: ResultCardConfig = {}
  ): Promise<GeneratedResultCard> {
    const {
      width = 600,
      height = 400,
      theme = 'auto',
      includeAvatar = false,
      includeBranding = true,
      customColors
    } = config;

    // Set canvas dimensions
    this.canvas.width = width;
    this.canvas.height = height;

    // Get theme colors
    const colors = this.getThemeColors(theme, customColors);

    // Clear canvas
    this.ctx.fillStyle = colors.background;
    this.ctx.fillRect(0, 0, width, height);

    // Draw background gradient
    await this.drawBackground(colors, width, height);

    // Draw header
    await this.drawHeader(result, colors, width);

    // Draw game board visualization (if final state available)
    if (result.finalState) {
      await this.drawTicTacToeBoard(result.finalState, colors, width, height);
    }

    // Draw player information
    await this.drawPlayerInfo(result, colors, width, height, includeAvatar);

    // Draw game statistics
    await this.drawGameStats(result, colors, width, height);

    // Draw branding
    if (includeBranding) {
      await this.drawBranding(colors, width, height);
    }

    // Convert to blob and data URL
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        const dataUrl = this.canvas.toDataURL('image/png');
        resolve({
          dataUrl,
          blob: blob!,
          width,
          height
        });
      }, 'image/png', 0.9);
    });
  }

  /**
   * Generate a generic game result card
   */
  public async generateGenericCard(
    result: GameResult,
    config: ResultCardConfig = {}
  ): Promise<GeneratedResultCard> {
    const {
      width = 600,
      height = 400,
      theme = 'auto',
      includeAvatar = false,
      includeBranding = true,
      customColors
    } = config;

    // Set canvas dimensions
    this.canvas.width = width;
    this.canvas.height = height;

    // Get theme colors
    const colors = this.getThemeColors(theme, customColors);

    // Clear canvas
    this.ctx.fillStyle = colors.background;
    this.ctx.fillRect(0, 0, width, height);

    // Draw background gradient
    await this.drawBackground(colors, width, height);

    // Draw header
    await this.drawHeader(result, colors, width);

    // Draw player information
    await this.drawPlayerInfo(result, colors, width, height, includeAvatar);

    // Draw game statistics
    await this.drawGameStats(result, colors, width, height);

    // Draw branding
    if (includeBranding) {
      await this.drawBranding(colors, width, height);
    }

    // Convert to blob and data URL
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        const dataUrl = this.canvas.toDataURL('image/png');
        resolve({
          dataUrl,
          blob: blob!,
          width,
          height
        });
      }, 'image/png', 0.9);
    });
  }

  /**
   * Get theme colors based on preference
   */
  private getThemeColors(theme: 'light' | 'dark' | 'auto', customColors?: any) {
    const isDark = theme === 'dark' || 
      (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const defaultColors = isDark ? {
      primary: '#25D366',
      secondary: '#128C7E',
      accent: '#FFD700',
      background: '#1a1a1a',
      text: '#ffffff',
      textSecondary: '#cccccc',
      cardBackground: '#2a2a2a',
      border: '#404040'
    } : {
      primary: '#25D366',
      secondary: '#128C7E',
      accent: '#FF9800',
      background: '#ffffff',
      text: '#333333',
      textSecondary: '#666666',
      cardBackground: '#f8f9fa',
      border: '#e0e0e0'
    };

    return { ...defaultColors, ...customColors };
  }

  /**
   * Draw background gradient
   */
  private async drawBackground(colors: any, width: number, height: number): Promise<void> {
    const gradient = this.ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, colors.background);
    gradient.addColorStop(1, colors.cardBackground);
    
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);

    // Add subtle pattern
    this.ctx.globalAlpha = 0.05;
    for (let i = 0; i < width; i += 20) {
      this.ctx.strokeStyle = colors.primary;
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(i, 0);
      this.ctx.lineTo(i + height, height);
      this.ctx.stroke();
    }
    this.ctx.globalAlpha = 1;
  }

  /**
   * Draw header with game type and result
   */
  private async drawHeader(result: GameResult, colors: any, width: number): Promise<void> {
    const centerX = width / 2;
    
    // Game type
    this.ctx.fillStyle = colors.textSecondary;
    this.ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText(result.gameType.toUpperCase(), centerX, 40);

    // Result status
    const isWin = result.winner !== null;
    const isDraw = result.winner === null && result.players.length > 1;
    
    let statusText = '';
    let statusColor = colors.text;
    
    if (isWin) {
      statusText = `🏆 ${result.winner} Wins!`;
      statusColor = colors.accent;
    } else if (isDraw) {
      statusText = '🤝 It\'s a Draw!';
      statusColor = colors.secondary;
    } else {
      statusText = '🎮 Game Complete';
      statusColor = colors.primary;
    }

    this.ctx.fillStyle = statusColor;
    this.ctx.font = 'bold 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    this.ctx.fillText(statusText, centerX, 80);
  }

  /**
   * Draw Tic-Tac-Toe board visualization
   */
  private async drawTicTacToeBoard(
    finalState: any,
    colors: any,
    width: number,
    height: number
  ): Promise<void> {
    const boardSize = Math.min(width * 0.3, 120);
    const cellSize = boardSize / 3;
    const startX = (width - boardSize) / 2;
    const startY = 120;

    // Draw grid
    this.ctx.strokeStyle = colors.border;
    this.ctx.lineWidth = 2;
    
    // Vertical lines
    for (let i = 1; i < 3; i++) {
      this.ctx.beginPath();
      this.ctx.moveTo(startX + i * cellSize, startY);
      this.ctx.lineTo(startX + i * cellSize, startY + boardSize);
      this.ctx.stroke();
    }
    
    // Horizontal lines
    for (let i = 1; i < 3; i++) {
      this.ctx.beginPath();
      this.ctx.moveTo(startX, startY + i * cellSize);
      this.ctx.lineTo(startX + boardSize, startY + i * cellSize);
      this.ctx.stroke();
    }

    // Draw X's and O's
    this.ctx.font = `bold ${cellSize * 0.6}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    if (Array.isArray(finalState)) {
      for (let i = 0; i < 9; i++) {
        const row = Math.floor(i / 3);
        const col = i % 3;
        const x = startX + col * cellSize + cellSize / 2;
        const y = startY + row * cellSize + cellSize / 2;
        
        if (finalState[i] === 'X') {
          this.ctx.fillStyle = colors.primary;
          this.ctx.fillText('✕', x, y);
        } else if (finalState[i] === 'O') {
          this.ctx.fillStyle = colors.secondary;
          this.ctx.fillText('○', x, y);
        }
      }
    }

    // Draw winning line if available
    if (finalState.winningLine) {
      this.drawWinningLine(finalState.winningLine, startX, startY, cellSize, colors);
    }
  }

  /**
   * Draw winning line on the board
   */
  private drawWinningLine(
    winningLine: number[],
    startX: number,
    startY: number,
    cellSize: number,
    colors: any
  ): void {
    if (winningLine.length !== 2) return;

    const [start, end] = winningLine;
    const startRow = Math.floor(start / 3);
    const startCol = start % 3;
    const endRow = Math.floor(end / 3);
    const endCol = end % 3;

    const x1 = startX + startCol * cellSize + cellSize / 2;
    const y1 = startY + startRow * cellSize + cellSize / 2;
    const x2 = startX + endCol * cellSize + cellSize / 2;
    const y2 = startY + endRow * cellSize + cellSize / 2;

    this.ctx.strokeStyle = colors.accent;
    this.ctx.lineWidth = 4;
    this.ctx.lineCap = 'round';
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  /**
   * Draw player information
   */
  private async drawPlayerInfo(
    result: GameResult,
    colors: any,
    width: number,
    height: number,
    includeAvatar: boolean
  ): Promise<void> {
    const startY = height - 120;
    const playerWidth = width / result.players.length;

    this.ctx.textAlign = 'center';
    
    result.players.forEach((player, index) => {
      const x = playerWidth * index + playerWidth / 2;
      const isWinner = player.name === result.winner;
      
      // Player name
      this.ctx.fillStyle = isWinner ? colors.accent : colors.text;
      this.ctx.font = `${isWinner ? 'bold ' : ''}16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      this.ctx.fillText(player.name, x, startY);
      
      // Winner crown
      if (isWinner) {
        this.ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        this.ctx.fillText('👑', x, startY - 25);
      }
      
      // Score (if available)
      if (player.score !== undefined) {
        this.ctx.fillStyle = colors.textSecondary;
        this.ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        this.ctx.fillText(`Score: ${player.score}`, x, startY + 20);
      }
    });
  }

  /**
   * Draw game statistics
   */
  private async drawGameStats(
    result: GameResult,
    colors: any,
    width: number,
    height: number
  ): Promise<void> {
    const statsY = height - 60;
    const centerX = width / 2;
    
    // Duration
    const minutes = Math.floor(result.duration / 60);
    const seconds = result.duration % 60;
    const durationText = `⏱️ ${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    this.ctx.fillStyle = colors.textSecondary;
    this.ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText(durationText, centerX, statsY);

    // Achievements (if any)
    if (result.achievements && result.achievements.length > 0) {
      const achievementText = `🎖️ ${result.achievements.length} achievement${result.achievements.length > 1 ? 's' : ''}`;
      this.ctx.fillText(achievementText, centerX, statsY + 20);
    }
  }

  /**
   * Draw branding
   */
  private async drawBranding(colors: any, width: number, height: number): Promise<void> {
    const brandingY = height - 20;
    const centerX = width / 2;
    
    this.ctx.fillStyle = colors.textSecondary;
    this.ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('🎮 PlayHub Games AI', centerX, brandingY);
  }

  /**
   * Generate a simple text-based result card (fallback)
   */
  public generateTextCard(result: GameResult): string {
    const isWin = result.winner !== null;
    const isDraw = result.winner === null && result.players.length > 1;
    
    let card = `🎮 ${result.gameType.toUpperCase()} RESULT 🎮\n\n`;
    
    if (isWin) {
      card += `🏆 WINNER: ${result.winner} 🏆\n\n`;
    } else if (isDraw) {
      card += `🤝 IT'S A DRAW! 🤝\n\n`;
    } else {
      card += `🎯 GAME COMPLETE 🎯\n\n`;
    }
    
    card += `👥 PLAYERS:\n`;
    result.players.forEach((player, index) => {
      const position = player.name === result.winner ? '🥇' : 
                     isDraw ? '🤝' : 
                     index === 0 ? '🥈' : '🥉';
      card += `${position} ${player.name}`;
      if (player.score !== undefined) {
        card += ` - ${player.score} pts`;
      }
      card += '\n';
    });
    
    const minutes = Math.floor(result.duration / 60);
    const seconds = result.duration % 60;
    card += `\n⏱️ Duration: ${minutes}:${seconds.toString().padStart(2, '0')}\n`;
    
    if (result.achievements && result.achievements.length > 0) {
      card += `🎖️ Achievements: ${result.achievements.length}\n`;
    }
    
    card += `\n🎮 PlayHub Games AI`;
    
    return card;
  }

  /**
   * Check if canvas is supported
   */
  public isCanvasSupported(): boolean {
    return !!(this.canvas && this.ctx);
  }
}

// Export singleton instance
export const resultCardGenerator = ResultCardGenerator.getInstance();